import { useCallback, useEffect, useRef, useState } from "react"
import ReactQuill, { Quill } from "react-quill-new"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ToggleCard } from "components/inputs/ToggleCard"

import { enumTenant, tenantOptions } from "constants/bonus"
import { enumCMSTextTypes } from "constants/cms"
import { getLanguageName } from "constants/languages"

import { CMSService } from "services/cms/cms.service"
import { CMSPoliciesStatus, CMSTextActionResponse } from "services/cms/type"
import { settingsService } from "services/settings/settings.service"

import { CustomRedo, CustomUndo } from "./QuillIcons/QuillIcons"

import "react-quill-new/dist/quill.snow.css"
import "css/quill-editor.css"

const icons = Quill.import("ui/icons") as Record<string, string>
icons["undo"] = `<svg viewBox="0 0 18 18"><path d="M6 4L2 8l4 4V9h7a4 4 0 0 1 0 8H8" /></svg>`
icons["redo"] = `<svg viewBox="0 0 18 18"><path d="M12 4l4 4-4 4v-3H5a4 4 0 0 0 0 8h7" /></svg>`
icons[
  "table"
] = `<svg viewBox="0 0 18 18"><rect x="2" y="2" width="14" height="14" fill="none" stroke="currentColor"/><line x1="2" y1="7" x2="16" y2="7" stroke="currentColor"/><line x1="2" y1="12" x2="16" y2="12" stroke="currentColor"/><line x1="7" y1="2" x2="7" y2="16" stroke="currentColor"/><line x1="12" y1="2" x2="12" y2="16" stroke="currentColor"/></svg>`

// Quill's own first-party "table" module ships built into quill@2.x (registered by default),
// unlike the abandoned quill-better-table addon. No manual Quill.register() needed for it.
type TableModule = { insertTable: (rows: number, columns: number) => void }

const quillModules = {
  toolbar: {
    container: [
      // ["undo", "redo"],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ background: [] }],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link", "table"],
      ["clean"]
    ],
    handlers: {
      undo: CustomUndo,
      redo: CustomRedo,
      table: function tableHandler(this: { quill: InstanceType<typeof Quill> }) {
        const tableModule = this.quill.getModule("table") as TableModule
        tableModule.insertTable(3, 3)
      }
    }
  },
  table: true,
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true
  }
}

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "table",
  "table-row",
  "table-body",
  "table-container"
]

export const CMSTextEditor = () => {
  const { t } = useTranslation(["translation"], { useSuspense: false })

  const [content, setContent] = useState("")
  const [tenantId, setTenantId] = useState(enumTenant.Slotwhales)
  const [textType, setTextType] = useState<enumCMSTextTypes>(enumCMSTextTypes.TERMS_CONTENT)
  const [locale, setLocale] = useState("en")
  const [cmsTextData, setCMSTextData] = useState<CMSTextActionResponse | null>(null)
  const [policiesStatus, setPoliciesStatus] = useState<CMSPoliciesStatus>({})
  const [togglingType, setTogglingType] = useState<string | null>(null)
  const [localeOptions, setLocaleOptions] = useState<{ name: string; value: string }[]>([{ name: "English", value: "en" }])
  // All texts for the current type+tenant, used as local cache to avoid re-fetching on locale change
  const [allTextsForType, setAllTextsForType] = useState<CMSTextActionResponse[]>([])
  const editorRef = useRef<ReactQuill | null>(null)

  const cmsTextTypeOptions = Object.values(enumCMSTextTypes).map((type) => ({
    name: t(`cms.policyTypes.${type}`),
    value: type
  }))

  // Fetch policies status + enabled languages in parallel on mount
  useEffect(() => {
    Promise.all([fetchPoliciesStatus(), fetchLocaleOptions()])
  }, [])

  // Re-fetch all texts for the selected type+tenant whenever either changes (1 request)
  useEffect(() => {
    if (!tenantId) {
      setAllTextsForType([])
      return
    }
    loadAllTextsForType()
  }, [tenantId, textType])

  // Derive displayed content from the cache — no network request
  useEffect(() => {
    const match = allTextsForType.find((item) => item.locale === locale && item.active)
    if (match) {
      setCMSTextData(match)
      setContent(match.content)
    } else {
      setCMSTextData(null)
      setContent("")
    }
  }, [locale, allTextsForType])

  const fetchLocaleOptions = async () => {
    const settings = await settingsService.getAll()
    if (!settings?.languages) return

    const enabled = Object.entries(settings.languages)
      .filter(([, enabled]) => enabled)
      .map(([code]) => ({ name: getLanguageName(code), value: code }))

    if (enabled.length) setLocaleOptions(enabled)
  }

  const loadAllTextsForType = async () => {
    try {
      // No locale / active filters — fetch every text for this type+tenant
      const res: any = await CMSService.getCMSTextData({ type: textType, tenant: tenantId })
      const all: CMSTextActionResponse[] = res ?? []
      setAllTextsForType(all)

      const enExists = all.some((item) => item.locale === "en")
      // Lock dropdown to EN if no EN base exists yet
      if (!enExists) setLocale("en")
    } catch {
      toast.error("Failed to load data")
    }
  }

  const fetchPoliciesStatus = async () => {
    const res = await CMSService.getPoliciesStatus()
    if (res) setPoliciesStatus(res)
  }

  const handleSave = async () => {
    if (!content || !tenantId) return toast.error("Please complete the inputs")

    const payload = {
      type: textType,
      content,
      active: true,
      tenant_id: tenantId,
      locale
    }

    const res = cmsTextData ? await CMSService.editCMSText(cmsTextData.id, payload) : await CMSService.createCMSText(payload)

    if (res) {
      toast.success(t("cms.policyEditor.savedSuccess") as string)
      setCMSTextData(res)
      // Update local cache directly — no extra request
      setAllTextsForType((prev) => [...prev.filter((item) => item.id !== res.id), res])
      // Refresh toggle status since a new active text may have appeared
      fetchPoliciesStatus()
    }
  }

  const handleTogglePolicy = useCallback(
    async (type: enumCMSTextTypes) => {
      if (togglingType) return
      const currentActive = !!policiesStatus[type]
      const nextActive = !currentActive

      setTogglingType(type)
      setPoliciesStatus((prev) => ({ ...prev, [type]: nextActive }))

      const ok = await CMSService.setPolicyTypeActive(type, nextActive)

      if (!ok) {
        // Revert optimistic update on failure
        setPoliciesStatus((prev) => ({ ...prev, [type]: currentActive }))
      }
      // Stored state is authoritative — optimistic update matches the DB write, no verify needed

      setTogglingType(null)
    },
    [policiesStatus, togglingType, t]
  )

  return (
    <>
      <div className="flex flex-col gap-3 mb-6">
        <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300">{t("cms.policyEditor.policyVisibility")}</h3>
        <div className="rounded-lg border border-info/40 bg-info/20 px-4 py-3 text-sm text-info flex flex-col gap-1">
          <p>
            <span className="font-semibold">ON</span> — {t("cms.policyEditor.infoToggleOn")}
          </p>
          <p>
            <span className="font-semibold">OFF</span> — {t("cms.policyEditor.infoToggleOff")}
          </p>
          <p className="mt-4">{t("cms.policyEditor.infoLocaleFallback")}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {Object.values(enumCMSTextTypes).map((type) => (
            <div key={type} className="min-w-[220px]">
              <ToggleCard
                label={t(`cms.policyTypes.${type}`)}
                checked={!!policiesStatus[type]}
                onChange={() => handleTogglePolicy(type)}
                disabled={togglingType === type}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 mb-4">
        <div className="flex-1">
          <DropdownMenu
            title={t("cms.policyEditor.policyType")}
            options={cmsTextTypeOptions}
            value={textType}
            onChange={(e: any) => setTextType(e.target.value)}
            disableEmptyOption
          />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <DropdownMenu
            title={t("cms.policyEditor.language")}
            options={localeOptions}
            value={locale}
            onChange={(e: any) => setLocale(e.target.value)}
            disableEmptyOption
          />
        </div>
        <div className="flex-1">
          <DropdownMenu
            title="Tenant"
            options={tenantOptions}
            value={tenantId}
            onChange={(e: any) => setTenantId(e.target.value)}
            placeholder="Please choose an option"
            altLabelOne={!tenantId ? "This field is required" : ""}
          />
        </div>
      </div>

      <ReactQuill
        ref={editorRef}
        theme="snow"
        modules={quillModules}
        formats={quillFormats}
        value={content}
        onChange={setContent}
        style={{
          height: 500,
          marginBottom: "2rem",
          background: "#FFFFFF",
          color: "#000000",
          borderRadius: "10px",
          border: "1px solid rgba(0, 0, 0, 0.4)"
        }}
      />
      <div className="flex flex-row items-end justify-end">
        <button className="btn btn-primary" onClick={handleSave}>
          {t("common.save")}
        </button>
      </div>
    </>
  )
}
